'use client'

import type { OnMount } from '@monaco-editor/react'
import { Box, Typography } from '@mui/material'
import { monoFont, spacing } from 'c1ui'
import { C1CircularProgress } from 'c1ui/components/C1CircularProgress/C1CircularProgress'
import { editorBackgroundColor, editorTheme } from 'components/core/editor/editorTheme'
import { initMonacoWorkers } from 'components/core/editor/monacoWorkers'
import type monaco from 'monaco-editor'
import dynamic, { DynamicOptionsLoadingProps } from 'next/dynamic'
import { FC, useCallback, useState } from 'react'
import { FormattedMessage } from 'react-intl'

export interface ReadOnlyCodeViewerProps {
    /** The content to display */
    value: string
    /** Language for syntax highlighting (e.g. 'json', 'yaml') */
    language: string
    /** Maximum height before scrolling (default: '500px') */
    maxHeight?: string
}

const Loading = (_loadingProps: DynamicOptionsLoadingProps): React.ReactElement => (
    <Box textAlign="center">
        <C1CircularProgress sx={{ color: 'grey.600' }} />
        <br />
        <Typography color="grey.600">
            <FormattedMessage id="loading_items" defaultMessage="Loading..." />
        </Typography>
    </Box>
)

const defaultOptions: monaco.editor.IStandaloneEditorConstructionOptions = {
    readOnly: true,
    domReadOnly: true,
    minimap: { enabled: false },
    wordWrap: 'on',
    lineNumbers: 'on',
    automaticLayout: true,
    contextmenu: false,
    links: false,
    folding: true,
    overviewRulerBorder: false,
    overviewRulerLanes: 0,
    hideCursorInOverviewRuler: true,
    scrollBeyondLastLine: false,
    lineNumbersMinChars: 3,
    renderLineHighlight: 'none',
    fontFamily: monoFont.style.fontFamily,
    fontSize: 14,
    scrollbar: {
        alwaysConsumeMouseWheel: false,
    },
}

/**
 * A lightweight, read-only Monaco editor for displaying structured content
 * (JSON, YAML, etc.) with syntax highlighting and code folding.
 *
 * Reuses the c1 theme and editor conventions from DynamicEditor but without
 * CEL-specific features, validation, or Redux dependencies — safe to use on
 * unauthenticated pages.
 *
 * @example
 * ```tsx
 * <ReadOnlyCodeViewer value={jsonString} language="json" />
 * ```
 */
export const ReadOnlyCodeViewer: FC<ReadOnlyCodeViewerProps> = dynamic(async () => {
    const [{ Editor, loader }, monacoModule] = await Promise.all([
        import('@monaco-editor/react'),
        import('monaco-editor'),
    ])

    initMonacoWorkers()
    loader.config({ monaco: monacoModule })
    monacoModule.editor.defineTheme('c1', editorTheme)

    return ({ value, language, maxHeight = '500px' }: ReadOnlyCodeViewerProps): React.ReactElement => {
        const [contentHeight, setContentHeight] = useState<number | null>(null)

        const onMount: OnMount = useCallback((editor) => {
            setContentHeight(editor.getContentHeight())
            editor.onDidContentSizeChange((e) => {
                if (e.contentHeightChanged) {
                    setContentHeight(e.contentHeight)
                }
            })
        }, [])

        return (
            <Box
                sx={{
                    border: '1px solid',
                    borderColor: 'grey.200',
                    borderRadius: 1,
                    overflow: 'hidden',
                    backgroundColor: editorBackgroundColor,
                }}
            >
                <Box
                    sx={{
                        p: 1,
                        height: contentHeight !== null
                            ? `calc(${contentHeight}px + (${spacing.xs} * 2))`
                            : 'auto',
                        maxHeight,
                        minHeight: '60px',
                    }}
                >
                    <Editor
                        height="100%"
                        language={language}
                        value={value}
                        options={defaultOptions}
                        theme="c1"
                        onMount={onMount}
                    />
                </Box>
            </Box>
        )
    }
}, { ssr: false, loading: Loading }) as FC<ReadOnlyCodeViewerProps>
